import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { createContext, useEffect, useState } from 'react';
import { useAuth } from './use-auth.js';
/*
 * The server is the authority: every route behind a hidden link still 404s, so a stale SPA can at
 * worst show a link. Staleness is bounded by `staleTime` (60s) plus a refetch on window focus.
 */
export const RELEASE_FLAGS_QUERY_KEY = ['release-flags'];
export const CLOSED_RELEASE_FLAGS = {
    flags: null,
    isPending: false,
    isError: false,
};
export const ReleaseFlagsContext = createContext(CLOSED_RELEASE_FLAGS);
/**
 * Mounted only with a session (a signed-out tab never registers a disabled query in the cache), and as a
 * sibling of `children` so a sign-in or sign-out never remounts the app tree. Publishes its result upward.
 */
function ReleaseFlagsFetcher({ api, session, scope, onState, }) {
    const query = useQuery({
        queryKey: [...RELEASE_FLAGS_QUERY_KEY, session.me.tenantId, session.me.userId],
        queryFn: async () => (await api.auth.releaseFlags()).flags,
        staleTime: 60_000,
        refetchOnWindowFocus: true,
    });
    const { data, isPending, isError } = query;
    useEffect(() => {
        onState({ scope, state: { flags: data ?? null, isPending, isError } });
    }, [onState, scope, data, isPending, isError]);
    useEffect(() => () => onState(null), [onState]);
    return null;
}
export function ReleaseFlagsProvider({ api, children }) {
    const { session } = useAuth();
    const [published, setPublished] = useState(null);
    const scope = session === null ? null : `${session.me.tenantId ?? ''}:${session.me.userId}`;
    let state = CLOSED_RELEASE_FLAGS;
    if (scope !== null) {
        state =
            published?.scope === scope
                ? published.state
                : { flags: null, isPending: true, isError: false };
    }
    return (_jsxs(ReleaseFlagsContext, { value: state, children: [session !== null && scope !== null ? (_jsx(ReleaseFlagsFetcher, { api: api, session: session, scope: scope, onState: setPublished })) : null, children] }));
}
//# sourceMappingURL=release-flags-provider.js.map